import React, { useState, useEffect } from 'react';
import { leadsAPI, usersAPI } from '../../services/api';

function AdminDashboard() {
  const [stats, setStats] = useState({
    totalLeads: 0,
    totalUsers: 0,
    byStatus: [],
    bySource: [],
    userAssignments: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const [leadsStatsResponse, usersResponse, leadsResponse] = await Promise.all([
        leadsAPI.getLeadStats(),
        usersAPI.getAllUsers({ limit: 100 }),
        leadsAPI.getAllLeads({ limit: 1000 })
      ]);
      
      const users = usersResponse.data.users || usersResponse.data;
      const leads = leadsResponse.data.leads || leadsResponse.data;
      
      // Calculate user assignments
      const userAssignments = users.map(user => {
        const leadCount = leads.filter(lead => 
          lead.primaryAssignee?._id === user._id || 
          lead.secondaryAssignees?.some(assignee => assignee._id === user._id)
        ).length || 0;
        return { _id: user._id, username: user.username, leadCount };
      }).sort((a, b) => b.leadCount - a.leadCount);
      
      setStats({
        ...leadsStatsResponse.data,
        totalUsers: users.length,
        userAssignments
      });
      setError('');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Closed Won': return 'bg-green-100 text-green-800';
      case 'Closed Lost': return 'bg-red-100 text-red-800';
      case 'Cold Lead': return 'bg-gray-100 text-gray-800';
      case 'Prospecting': return 'bg-blue-100 text-blue-800';
      case 'Qualified': return 'bg-emerald-100 text-emerald-800';
      case 'Demo Scheduled / Done': return 'bg-purple-100 text-purple-800';
      case 'Evaluation / Pilot': return 'bg-indigo-100 text-indigo-800';
      case 'Decision': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="bg-white p-6 rounded-lg shadow">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-24 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }


  return (
    <div className="min-h-screen bg-gray-50 p-3 sm:p-4 md:p-6 lg:p-8">
      <div className="max-w-7xl mx-auto">
        <div className="bg-white p-4 sm:p-6 lg:p-8 rounded-xl sm:rounded-2xl shadow-lg sm:shadow-xl border border-gray-200/50">
          {/* Header */}
          <div className="mb-6 sm:mb-8">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-2">
              Super Admin Dashboard
            </h2>
            <p className="text-sm sm:text-base text-gray-600">
              Welcome back! Here's an overview of your CRM system.
            </p>
          </div>
          
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-3 sm:px-4 py-2 sm:py-3 rounded text-sm sm:text-base mb-4 sm:mb-6">
              {error}
            </div>
          )}

          {/* Key Metrics - Responsive Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-6 mb-6 sm:mb-8">
            {/* Total Leads */}
            <div className="bg-blue-50 p-4 sm:p-5 md:p-6 rounded-lg border border-blue-200 hover:shadow-md transition-shadow">
              <div className="flex items-center">
                <div className="p-2 sm:p-3 rounded-full bg-blue-100 mr-3 sm:mr-4 flex-shrink-0">
                  <svg className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs sm:text-sm font-medium text-blue-600 truncate">Total Leads</p>
                  <p className="text-xl sm:text-2xl md:text-3xl font-bold text-blue-900">{stats.totalLeads}</p>
                </div>
              </div>
            </div>

            {/* Total Users */}
            <div className="bg-green-50 p-4 sm:p-5 md:p-6 rounded-lg border border-green-200 hover:shadow-md transition-shadow">
              <div className="flex items-center">
                <div className="p-2 sm:p-3 rounded-full bg-green-100 mr-3 sm:mr-4 flex-shrink-0">
                  <svg className="w-5 h-5 sm:w-6 sm:h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                  </svg>
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs sm:text-sm font-medium text-green-600 truncate">Total Users</p>
                  <p className="text-xl sm:text-2xl md:text-3xl font-bold text-green-900">{stats.totalUsers}</p>
                </div>
              </div>
            </div>

            {/* Won Leads */}
            <div className="bg-yellow-50 p-4 sm:p-5 md:p-6 rounded-lg border border-yellow-200 hover:shadow-md transition-shadow">
              <div className="flex items-center">
                <div className="p-2 sm:p-3 rounded-full bg-yellow-100 mr-3 sm:mr-4 flex-shrink-0">
                  <svg className="w-5 h-5 sm:w-6 sm:h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs sm:text-sm font-medium text-yellow-600 truncate">Won Leads</p>
                  <p className="text-xl sm:text-2xl md:text-3xl font-bold text-yellow-900">
                    {stats.byStatus.find(s => s._id === 'Closed Won')?.count || 0}
                  </p>
                </div>
              </div>
            </div>

            {/* Conversion Rate */}
            <div className="bg-purple-50 p-4 sm:p-5 md:p-6 rounded-lg border border-purple-200 hover:shadow-md transition-shadow">
              <div className="flex items-center">
                <div className="p-2 sm:p-3 rounded-full bg-purple-100 mr-3 sm:mr-4 flex-shrink-0">
                  <svg className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                  </svg>
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs sm:text-sm font-medium text-purple-600 truncate">Conversion Rate</p>
                  <p className="text-xl sm:text-2xl md:text-3xl font-bold text-purple-900">
                    {stats.totalLeads > 0 
                      ? Math.round(((stats.byStatus.find(s => s._id === 'Closed Won')?.count || 0) / stats.totalLeads) * 100)
                      : 0}%
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Charts Section - Responsive Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
            {/* Lead Status Distribution */}
            <div className="bg-gray-50 p-4 sm:p-5 md:p-6 rounded-lg border border-gray-200">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">
                Lead Status Distribution
              </h3>
              <div className="space-y-2 sm:space-y-3">
                {stats.byStatus.map((status) => (
                  <div key={status._id} className="flex items-center justify-between gap-3">
                    <span className={`px-2.5 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium ${getStatusColor(status._id)} truncate flex-shrink-0`}>
                      {status._id}
                    </span>
                    <div className="flex-1 bg-gray-200 rounded-full h-2 sm:h-2.5 mx-2 sm:mx-3">
                      <div 
                        className={`h-full rounded-full transition-all duration-300 ${getStatusColor(status._id).replace('100', '500').replace('text', 'bg')}`}
                        style={{ width: `${(status.count / stats.totalLeads) * 100}%` }}
                      ></div>
                    </div>
                    <span className="font-semibold text-gray-900 text-sm sm:text-base flex-shrink-0">
                      {status.count}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Lead Source Distribution */}
            <div className="bg-gray-50 p-4 sm:p-5 md:p-6 rounded-lg border border-gray-200">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">
                Lead Source Distribution
              </h3>
              <div className="space-y-2 sm:space-y-3">
                {stats.bySource.map((source) => (
                  <div key={source._id} className="flex items-center justify-between gap-3">
                    <span className="px-2.5 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium bg-indigo-100 text-indigo-800 truncate flex-shrink-0">
                      {source._id}
                    </span>
                    <div className="flex-1 bg-gray-200 rounded-full h-2 sm:h-2.5 mx-2 sm:mx-3">
                      <div 
                        className="h-full rounded-full bg-indigo-500 transition-all duration-300"
                        style={{ width: `${(source.count / stats.totalLeads) * 100}%` }}
                      ></div>
                    </div>
                    <span className="font-semibold text-gray-900 text-sm sm:text-base flex-shrink-0">
                      {source.count}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* User Lead Assignments */}
            <div className="bg-gray-50 p-4 sm:p-5 md:p-6 rounded-lg border border-gray-200">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">
                User Lead Assignments
              </h3>
              <div className="space-y-2 sm:space-y-3">
                {stats.userAssignments?.map((user) => (
                  <div key={user._id} className="flex items-center justify-between gap-3">
                    <span className="px-2.5 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium bg-emerald-100 text-emerald-800 truncate flex-shrink-0">
                      {user.username}
                    </span>
                    <div className="flex-1 bg-gray-200 rounded-full h-2 sm:h-2.5 mx-2 sm:mx-3">
                      <div 
                        className="h-full rounded-full bg-emerald-500 transition-all duration-300"
                        style={{ width: `${stats.totalLeads > 0 ? (user.leadCount / stats.totalLeads) * 100 : 0}%` }}
                      ></div>
                    </div>
                    <span className="font-semibold text-gray-900 text-sm sm:text-base flex-shrink-0">
                      {user.leadCount}
                    </span>
                  </div>
                )) || (
                  <p className="text-gray-500 text-sm">No user assignments found</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminDashboard;